import { useState, useEffect, useRef, Fragment } from "react";
import jsPDF from "jspdf";
import store from "../../../../app/store";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";
import { openPdfPreview } from "../printTest/GeneratePDF";
/* ═══════════════════════════════════════════════════
   DAILY SALES CHECK  —  DailySalesCheck.jsx

   Props:
     transactions — Array of raw transaction docs (todaysTransaction)
     computed     — Single object returned by salesCheckFunction(todaysTransaction)
                    { listOfItems, eachItemAndQuantity, revenueDistribution,
                      incomeBreakdown, totalDiscount, totalVAT }
     date         — ISO string for the report date
     onClose      — () => void
     onEmail      — () => void

   Usage:
     const computed = salesCheckFunction(todaysTransaction);
     <DailySalesCheck
       transactions={todaysTransaction}
       computed={computed}
       date={new Date().toISOString()}
       onClose={() => setOpen(false)}
       onEmail={() => handleEmail()}
     />
═══════════════════════════════════════════════════ */

/* ─── Helpers ─────────────────────────────────── */

const fmt = (n) =>
  `${Number(n || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtDateShort = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ─── Per-transaction summary ─────────────────────
   Build one summary row per transaction for the
   "Transactions Summary" table on the page & PDF
──────────────────────────────────────────────── */
function buildTxSummaries(transactions) {
  console.log("Building transaction summaries for:", transactions);

  return transactions.map((tx, idx) => {
    const op = tx.operationType || {};
    const td = tx.transactionDetails || {};
    const isLoan = op.whichOperation === "loan";
    const isReturned = td.deliveryStatus === "returned";

    const subTotal = isLoan ? (isReturned ? 0 : op.intialPayment || 0) : td.totalAmount || 0;

    const onLoan = isLoan && !isReturned ? op.balance || 0 : 0;

    return {
      idx,
      transID: tx.transID || tx._id || "—",
      customer: (tx.customer || {}).fullName || "Anonymous",
      soldBy: (tx.soldBy || {}).fullName || "—",
      date: td.date || tx.createdAt || "",
      paymentMode: td.paymentMode || "cash",
      payments: td.payments || [],
      deliveryStatus: td.deliveryStatus || "",
      operationType: op.whichOperation || "cash",
      itemCount: (tx.products || []).length,
      subTotal,
      onLoan,
      discount: td.discount || 0,
      vat: td.vat || 0,
    };
  });
}

/* ─── Payment-type styling — single source of truth for badges,
   revenue bars, breakdown rows, and the PDF. Keys match the schema's
   payments[].paymentType enum exactly:
   ["cash", "POS", "bankTransfer", "mobilePay", "card", "storelensePOS"]
   "split" is included here too since transactionDetails.paymentMode's
   enum allows it as an overall mode — but it is never itself a revenue
   bucket (a split transaction's amount is decomposed into the concrete
   types above), so it's excluded from PAYMENT_TYPE_ORDER. ─── */
const PAYMENT_TYPE_STYLE = {
  cash: { label: "Cash", color: "#16a34a", bg: "#dcfce7" },
  POS: { label: "POS", color: "#4f46e5", bg: "#e0e7ff" },
  bankTransfer: { label: "Bank Transfer", color: "#ea580c", bg: "#ffedd5" },
  mobilePay: { label: "MobilePay", color: "#0d9488", bg: "#ccfbf1" },
  card: { label: "Card", color: "#0369a1", bg: "#f0f9ff" },
  storelensePOS: { label: "StoreLense POS", color: "#475569", bg: "#f1f5f9" },
  split: { label: "Split", color: "#c026d3", bg: "#fae8ff" },
};

// Revenue buckets — mirrors payments[].paymentType exactly, in schema order.
const PAYMENT_TYPE_ORDER = ["cash", "POS", "bankTransfer", "mobilePay", "card", "storelensePOS"];

// Badge styles for things that are not payment types (sale type / status).
const NON_PAYMENT_BADGE_STYLE = {
  loan: { label: "Loan", color: "#d97706", bg: "#fef3c7" },
  returned: { label: "Returned", color: "#dc2626", bg: "#fee2e2" },
  supplied: { label: "Supplied", color: "#7c3aed", bg: "#ede9fe" },
  notSupplied: { label: "Not Supplied", color: "#6b7a99", bg: "#eef1f6" },
};

function resolveBadgeStyle(type) {
  return NON_PAYMENT_BADGE_STYLE[type] || PAYMENT_TYPE_STYLE[type] || PAYMENT_TYPE_STYLE.cash;
}

/* ─── Revenue distribution ─────────────────────────
   Rebuilt to match the schema exactly: always returns every bucket in
   PAYMENT_TYPE_ORDER (even at 0), built from each transaction's own
   `payments[]` entries. A split transaction's payments are decomposed
   so a Cash+POS+BankTransfer split correctly lands its amounts in all
   three buckets instead of only "cash". Falls back to paymentMode +
   subTotal for older transactions that predate `payments[]`.
──────────────────────────────────────────────── */
function buildRevenueDistribution(txSummaries) {
  const dist = {};
  PAYMENT_TYPE_ORDER.forEach((k) => {
    dist[k] = 0;
  });
  txSummaries.forEach((tx) => {
    if (tx.deliveryStatus === "returned") return;
    if (Array.isArray(tx.payments) && tx.payments.length) {
      tx.payments.forEach((p) => {
        const key = PAYMENT_TYPE_ORDER.includes(p.paymentType) ? p.paymentType : "cash";
        dist[key] += Number(p.amount) || 0;
      });
    } else {
      const key = PAYMENT_TYPE_ORDER.includes(tx.paymentMode) ? tx.paymentMode : "cash";
      dist[key] += Number(tx.subTotal) || 0;
    }
  });
  return dist;
}

/* ═══════════════════════════════════════════════
   PDF HTML BUILDER
═══════════════════════════════════════════════ */
function buildPDFHtml({ transactions, computed, date }) {
  const {
    listOfItems = [],
    eachItemAndQuantity = [],
    incomeBreakdown = { incomeFromSoldProducts: 0, amountOnLoan: 0 },
    totalDiscount = 0,
    totalVAT = 0,
  } = computed;

  const currency = (transactions[0] || {}).currency || "₦";
  const storeName = ((transactions[0] || {}).store || {}).name || "Store";
  const txSummaries = buildTxSummaries(transactions);
  const totalItemQty = eachItemAndQuantity.reduce((s, i) => s + (i.quantity || 0), 0);
  const revenueDistribution = buildRevenueDistribution(txSummaries);
  const revTotal = Object.values(revenueDistribution).reduce((s, v) => s + v, 0);
  /* const grandTotal = incomeBreakdown.incomeFromSoldProducts - (incomeBreakdown.amountOnLoan || 0); */

  const grandTotal =
    incomeBreakdown.incomeFromSoldProducts +
    /* Number(totalVAT || 0) + */
    Number(incomeBreakdown.clearedLoan || 0) -
    /*  Number(totalDiscount || 0) - */
    Number(incomeBreakdown.expenses || 0);

  const customerSet = new Set(transactions.map((tx) => (tx.customer || {}).fullName).filter(Boolean));

  const badgeHtml = (type) => {
    const e = resolveBadgeStyle(type);
    return `<span style="display:inline-block;background:${e.bg};color:${e.color};padding:2px 7px;border-radius:5px;font-size:10px;font-weight:700;">${e.label}</span>`;
  };

  // Small stacked list of the account(s) a transaction was paid through —
  // shown under the Payment badge for every transaction that has at least
  // one payments[] entry, not just split ones (the on-screen version is
  // click-to-expand instead).
  const paymentBreakdownHtml = (payments = []) =>
    `<div style="margin-top:4px;display:flex;flex-direction:column;gap:2px;">
      ${payments
        .map(
          (p) =>
            `<div style="font-size:8.5px;color:#6b7a99;white-space:nowrap;">${p.receivingAccountName || resolveBadgeStyle(p.paymentType).label} — ${fmt(p.amount)}</div>`,
        )
        .join("")}
    </div>`;

  const barHtml = (pct, color) =>
    `<div style="flex:1;background:#e2e8f2;border-radius:99px;height:5px;overflow:hidden;">
       <div style="width:${Math.min(pct, 100)}%;height:100%;background:${color};border-radius:99px;"></div>
     </div>`;

  const iqColors = [
    { bg: "#dbeafe", c: "#2563EB" },
    { bg: "#ccfbf1", c: "#0d9488" },
    { bg: "#fef3c7", c: "#d97706" },
    { bg: "#ede9fe", c: "#7c3aed" },
  ];

  const txRows = txSummaries
    .map((tx, i) => {
      const isRet = tx.deliveryStatus === "returned";
      return `
      <tr style="background:${i % 2 === 0 ? "#fff" : "#f7f9fd"};border-bottom:1px solid #e2e8f2;${isRet ? "opacity:0.55;" : ""}" >
        <td style="padding:7px 9px;font-size:10.5px;font-weight:700;color:#1e3a5f;">${tx.customer}</td>
        <td style="padding:7px 9px;font-size:10px;color:#6b7a99;">${fmtDate(tx.date)}</td>
        <td style="padding:7px 9px;text-align:center;">${badgeHtml(tx.operationType)}</td>
        <td style="padding:7px 9px;text-align:center;">${badgeHtml(tx.paymentMode)}${tx.payments && tx.payments.length ? paymentBreakdownHtml(tx.payments) : ""}</td>
        <td style="padding:7px 9px;text-align:center;font-size:10.5px;">${tx.itemCount}</td>
        <td style="padding:7px 9px;text-align:right;font-size:10.5px;font-weight:700;">${currency} ${fmt(tx.subTotal)}</td>
        <td style="padding:7px 9px;text-align:right;font-size:10px;color:${tx.onLoan > 0 ? "#d97706" : "#6b7a99"};">${
          tx.onLoan > 0 ? `${currency} ${fmt(tx.onLoan)}` : "—"
        }</td>
        <td style="padding:7px 9px;font-size:10px;">${tx.soldBy}</td>
        <td style="padding:7px 9px;text-align:center;">${isRet ? badgeHtml("returned") : "—"}</td>
      </tr>`;
    })
    .join("");

  //   ── Item rows ──
  const itemRows = listOfItems
    .map((item, idx) => {
      const isRet = item.returned || item.productIsNewlyReturned;
      const isSupp = item.supplied;
      return `
      <tr style="background:${isRet ? "#fff5f5" : idx % 2 === 0 ? "#fff" : "#f7f9fd"};opacity:${isRet ? 0.55 : 1};border-bottom:1px solid #e2e8f2;">
        <td style="padding:6px 9px;font-size:10.5px;font-weight:700;text-decoration:${isRet ? "line-through" : "none"};">
          <div style="display:flex;align-items:center;gap:6px;">
            ${
              item.productImage
                ? `<img src="${item.productImage}" style="width:22px;height:22px;border-radius:4px;object-fit:cover;border:1px solid #e2e8f2;" onerror="this.style.display='none'" crossorigin="anonymous"/>`
                : ""
            }
            <span style="max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${item.name || "—"}</span>
          </div>
        </td>
        <td style="padding:6px 8px;text-align:center;font-size:10.5px;">${item.quantity}</td>
        <td style="padding:6px 8px;text-align:right;font-size:10px;">${fmt(item.soldUnitPrice)}</td>
        <td style="padding:6px 8px;text-align:right;font-size:10.5px;font-weight:700;">${fmt(item.totalAmount)}</td>
        <td style="padding:6px 8px;text-align:center;">${badgeHtml(item.transactionType || "cash")}</td>
        <td style="padding:6px 8px;text-align:right;font-size:10px;color:${item.paid > 0 && !isRet ? "#16a34a" : "#1a2236"};">${fmt(item.paid)}</td>
        <td style="padding:6px 8px;text-align:right;font-size:10px;color:${item.balance > 0 ? "#d97706" : "#1a2236"};font-weight:${
          item.balance > 0 ? 700 : 400
        };">${fmt(item.balance)}</td>
        <td style="padding:6px 8px;text-align:center;">
          ${isRet ? badgeHtml("returned") : ""}
          ${isSupp ? badgeHtml("supplied") : ""}
          ${!isRet && !isSupp ? '<span style="color:#6b7a99;font-size:10px;">—</span>' : ""}
        </td>
      </tr>`;
    })
    .join("");

  /* ── IQ cards ── */
  const iqCards = eachItemAndQuantity
    .map((item, idx) => {
      const col = iqColors[idx % iqColors.length];
      const barColor = ["#2563EB", "#0d9488", "#d97706", "#7c3aed"][idx % 4];
      const pct = totalItemQty ? Math.round((item.quantity / totalItemQty) * 100) : 0;
      return `
      <div style="flex:1;min-width:130px;background:#fff;border:1px solid #e2e8f2;border-radius:10px;padding:11px 12px;">
        <div style="display:flex;align-items:center;gap:9px;margin-bottom:8px;">
          <div style="width:36px;height:36px;border-radius:50%;background:${col.bg};color:${
            col.c
          };display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:800;flex-shrink:0;">${item.quantity}</div>
          <div>
            <div style="font-size:11.5px;font-weight:700;color:#1e3a5f;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100px;">${
              item.name
            }</div>
            <div style="font-size:9.5px;color:#6b7a99;margin-top:2px;">${item.quantity} unit${item.quantity !== 1 ? "s" : ""}</div>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:5px;">
          ${barHtml(pct, barColor)}
          <span style="font-size:9.5px;font-weight:700;color:#6b7a99;white-space:nowrap;">${pct}%</span>
        </div>
      </div>`;
    })
    .join("");

  //   ── Revenue rows ──
  const revRowsHtml = [
    ...PAYMENT_TYPE_ORDER.filter((k) => revenueDistribution[k] > 0).map((k) => ({
      label: PAYMENT_TYPE_STYLE[k].label,
      val: revenueDistribution[k],
      color: PAYMENT_TYPE_STYLE[k].color,
    })),
    ...(totalVAT > 0 ? [{ label: "VAT", val: totalVAT, color: "#7c3aed" }] : []),
    ...(totalDiscount > 0 ? [{ label: "Discount", val: totalDiscount, color: "#dc2626" }] : []),
  ]
    .map(({ label, val, color }) => {
      const pct = revTotal > 0 ? Math.round((val / revTotal) * 100) : 0;
      return `
      <div style="background:#fff;border:1px solid #e2e8f2;border-left:4px solid ${color};border-radius:10px;padding:9px 12px;margin-bottom:7px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px;">
          <span style="font-size:9.5px;font-weight:800;color:#6b7a99;text-transform:uppercase;letter-spacing:.5px;">${label}</span>
          <span style="font-size:13px;font-weight:800;color:${color};">${currency} ${fmt(val)}</span>
        </div>
        <div style="display:flex;align-items:center;gap:5px;">${barHtml(
          pct,
          color,
        )}<span style="font-size:9.5px;font-weight:700;color:#6b7a99;white-space:nowrap;">${pct}%</span></div>
      </div>`;
    })
    .join("");

  //  ── Income rows ──
  const incRowsHtml = [
    { label: "Income from sold products", val: fmt(incomeBreakdown.incomeFromSoldProducts), lc: "#6b7a99", vc: "#1a2236" },
    ...(incomeBreakdown.amountOnLoan > 0 ? [{ label: "Amount on loan", val: fmt(incomeBreakdown.amountOnLoan), lc: "#d97706", vc: "#d97706" }] : []),
    ...(totalDiscount > 0 ? [{ label: "Total discount", val: `-${fmt(totalDiscount)}`, lc: "#dc2626", vc: "#dc2626" }] : []),
    ...(totalVAT > 0 ? [{ label: "VAT", val: `+${fmt(totalVAT)}`, lc: "#7c3aed", vc: "#7c3aed" }] : []),
    ...(incomeBreakdown.clearedLoan > 0 ? [{ label: "Cleared loan", val: `+${fmt(incomeBreakdown.clearedLoan)}`, lc: "#16a34a", vc: "#16a34a" }] : []),
  ]
    .map(
      ({ label, val, lc, vc }) => `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:7px 0;border-bottom:1px solid #e2e8f2;">
      <span style="font-size:11px;font-weight:600;color:${lc};">${label}</span>
      <span style="font-size:12px;font-weight:800;color:${vc};white-space:nowrap;">${currency} ${val}</span>
    </div>`,
    )
    .join("");

  // ── Stats strip items ──
  const stats = [
    { label: "Transactions", val: transactions.length, color: "#2563EB" },
    { label: "Customers", val: customerSet.size, color: "#0d9488" },
    { label: "Total Items", val: totalItemQty, color: "#d97706" },
    { label: "Grand Total", val: `${currency} ${fmt(grandTotal)}`, color: "#1e3a5f" },
    { label: "On Loan", val: `${currency} ${fmt(incomeBreakdown.amountOnLoan || 0)}`, color: "#d97706" },
    ...(totalVAT > 0 ? [{ label: "VAT", val: `${currency} ${fmt(totalVAT)}`, color: "#7c3aed" }] : []),
  ]
    .map(
      ({ label, val, color }) => `
    <div style="flex:1;padding:12px 8px;text-align:center;border-right:1px solid #e2e8f2;min-width:80px;">
      <div style="font-size:8.5px;font-weight:800;letter-spacing:.8px;text-transform:uppercase;color:#6b7a99;">${label}</div>
      <div style="font-size:14px;font-weight:800;color:${color};margin-top:4px;">${val}</div>
    </div>`,
    )
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap" rel="stylesheet"/>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Nunito',sans-serif;background:#fff;color:#1a2236;width:794px;-webkit-font-smoothing:antialiased;}
  table{border-collapse:collapse;width:100%;}
  th,td{vertical-align:middle;}
    .page-section,
.report-card,
table,
tr,
.footer {
  break-inside: avoid;
  page-break-inside: avoid;
}
</style>
</head>
<body>

<!-- HEADER -->
<div style="background:#1e3a5f;padding:16px 24px;display:flex;justify-content:space-between;align-items:center;">
  <div>
    <div style="color:#fff;font-size:17px;font-weight:800;">Daily Sales Report</div>
    <div style="color:#93c5fd;font-size:12px;font-weight:600;margin-top:3px;">${storeName}</div>
  </div>
  <div style="text-align:right;">
    <div style="color:#fff;font-size:13px;font-weight:700;">${fmtDateShort(new Date(date || new Date()).toISOString())}</div>
    <div style="color:#93c5fd;font-size:10px;margin-top:3px;">Generated ${fmtDate(new Date().toISOString())}</div>
  </div>
</div>

<!-- STATS STRIP -->
<div style="display:flex;background:#f7f9fd;border-bottom:2px solid #e2e8f2;flex-wrap:wrap;">
  ${stats}
</div>

<div style="padding:18px 24px;display:flex;flex-direction:column;gap:20px;">

  <!-- TRANSACTIONS SUMMARY -->
  <div class="page-section">
    <div style="font-size:9.5px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:#6b7a99;margin-bottom:9px;">Transactions Summary</div>
    <div style="border-radius:10px;border:1px solid #e2e8f2;overflow:hidden;">
      <table>
        <thead>
          <tr style="background:#1e3a5f;">
            ${["Customer", "Date & Time", "Sale Type", "Payment", "Items", "Sub-total", "On Loan", "Sold by", "Status"]
              .map(
                (h) =>
                  `<th style="padding:8px 9px;text-align:left;color:#fff;font-size:8.5px;font-weight:800;letter-spacing:.5px;text-transform:uppercase;white-space:nowrap;">${h}</th>`,
              )
              .join("")}
          </tr>
        </thead>
        <tbody>${txRows}</tbody>
      </table>
    </div>
  </div>

  <!-- ALL ITEMS SOLD -->
  <div class="page-section">
    <div style="font-size:9.5px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:#6b7a99;margin-bottom:9px;">All Items Sold</div>
    <div style="border-radius:10px;border:1px solid #e2e8f2;overflow:hidden;">
      <table>
        <thead>
          <tr style="background:#1e3a5f;">
            ${["Item", "Qty", `Unit (${currency})`, `Total (${currency})`, "Type", `Paid (${currency})`, `Balance ${currency}`, "Status"]
              .map(
                (h) =>
                  `<th style="padding:8px 9px;text-align:left;color:#fff;font-size:8.5px;font-weight:800;letter-spacing:.5px;text-transform:uppercase;white-space:nowrap;">${h}</th>`,
              )
              .join("")}
          </tr>
        </thead>
        <tbody>${itemRows}</tbody>
      </table>
    </div>
  </div>

  ${
    eachItemAndQuantity.length > 0
      ? `
  <!-- EACH ITEM & QTY -->
  <div class="page-section">
    <div style="font-size:9.5px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:#6b7a99;margin-bottom:9px;">Each Item and Sold Quantity</div>
    <div style="display:flex;flex-wrap:wrap;gap:10px;">${iqCards}</div>
    <div style="margin-top:10px;background:#1e3a5f;border-radius:10px;padding:10px 16px;display:flex;align-items:center;justify-content:space-between;">
      <span style="font-size:9.5px;font-weight:700;color:rgba(255,255,255,.6);letter-spacing:.6px;text-transform:uppercase;">Total Quantity</span>
      <span style="font-size:19px;font-weight:800;color:#fff;">${totalItemQty}</span>
    </div>
  </div>`
      : ""
  }

  <!-- INCOME + REVENUE -->
  <div class="page-section" style="display:flex;gap:14px;align-items:flex-start;">
    <div style="flex:1;background:#f7f9fd;border:1px solid #e2e8f2;border-radius:14px;padding:14px;">
      <div style="font-size:9.5px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:#6b7a99;margin-bottom:10px;">Income Breakdown (${currency})</div>
      ${incRowsHtml}
      <div style="margin-top:10px;background:#1e3a5f;border-radius:10px;padding:12px 14px;display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:9.5px;font-weight:700;letter-spacing:.7px;text-transform:uppercase;color:rgba(255,255,255,.6);">Grand Total</span>
        <span style="font-size:17px;font-weight:800;color:#fff;">${currency} ${fmt(grandTotal)}</span>
      </div>
    </div>
    <div style="flex:1;background:#f7f9fd;border:1px solid #e2e8f2;border-radius:14px;padding:14px;">
      <div style="font-size:9.5px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:#6b7a99;margin-bottom:10px;">Revenue Distribution (${currency})</div>
      ${revRowsHtml}
    </div>
  </div>

</div>

<!-- FOOTER -->
<!-- <div class="footer style="background:#f0f4fa;border-top:1px solid #e2e8f2;padding:10px 24px;display:flex;justify-content:space-between;align-items:center;">
  <span style="font-size:9.5px;color:#6b7a99;font-weight:600;">StoreLense  •  ${storeName}</span>
  <span style="font-size:9.5px;color:#6b7a99;font-weight:600;">${transactions.length} transaction${transactions.length !== 1 ? "s" : ""}  •  ${
    customerSet.size
  } customer${customerSet.size !== 1 ? "s" : ""}</span>
  <span style="font-size:9.5px;color:#6b7a99;font-weight:600;">${fmtDate(new Date().toISOString())}</span>
</div> 
-->

</body>
</html>`;
}

/* ----------------------------------------------
Helper funtion

--------------------------------
 */

async function loadScript(src, check) {
  if (check()) return;
  await new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function loadJsPDF() {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", () => !!window.html2canvas);
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", () => !!(window.jspdf && window.jspdf.jsPDF));
  return window.jspdf.jsPDF;
}

/* ═══════════════════════════════════════════════
   PDF DOWNLOAD
═══════════════════════════════════════════════ */
// REPLACE WITH THIS:

/* async function downloadPDF(transactions, computed, date, sendToEmail = false) {
  const jsPDF = await loadJsPDF();

  const html = buildPDFHtml({ transactions, computed, date });

  const iframe = document.createElement("iframe");
  iframe.style.cssText = ["position:fixed", "left:-9999px", "top:0", "width:794px", "height:10px", "border:none", "visibility:hidden"].join(";");

  document.body.appendChild(iframe);

  const customerSet = new Set(transactions.map((tx) => (tx.customer || {}).fullName).filter(Boolean));

  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  await new Promise((r) => setTimeout(r, 300));

  const iDoc = iframe.contentDocument;
  const iBody = iDoc.body;
  const iHtml = iDoc.documentElement;

  const fullH = Math.max(iBody.scrollHeight, iBody.offsetHeight, iHtml.scrollHeight, iHtml.offsetHeight);

  iframe.style.height = `${fullH}px`;

  await new Promise((r) => setTimeout(r, 800));

  const canvas = await window.html2canvas(iBody, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    width: 794,
    height: fullH,
    windowWidth: 794,
    windowHeight: fullH,
    backgroundColor: "#ffffff",
    imageTimeout: 3000,
  });

  document.body.removeChild(iframe);

  const A4_W = 210;
  const A4_H = 297;

  const FOOTER_H = 14;
  const BOTTOM_MARGIN = 8;
  const TOP_MARGIN = 8;
  const CONTENT_H = A4_H - FOOTER_H - BOTTOM_MARGIN - TOP_MARGIN;

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const imgW = canvas.width;
  const imgH = canvas.height;

  const storeNameRaw = ((transactions[0] || {}).store || {}).name || "store";

  const footerLeft = `StoreLense • ${storeNameRaw}`;
  const footerCenter = `${transactions.length} transactions • ${customerSet.size || 0} customer${customerSet.size !== 1 ? "s" : ""}`;

  const footerRight = new Date().toLocaleString();

  

  const pxPerPage = Math.floor((CONTENT_H / A4_W) * imgW);
  const minSliceH = Math.floor(pxPerPage * 0.65);

  function isMostlyWhiteRow(ctx, y, width) {
    const data = ctx.getImageData(0, y, width, 1).data;
    let white = 0;

    for (let x = 0; x < width; x++) {
      const i = x * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (r > 245 && g > 245 && b > 245) white++;
    }

    return white / width > 0.92;
  }

  function findSafeCut(ctx, startY, idealCutY, maxY, width) {
    const searchUpTo = 220; // pixels upward to search for whitespace

    for (let y = idealCutY; y > idealCutY - searchUpTo && y > startY + minSliceH; y--) {
      if (isMostlyWhiteRow(ctx, y, width)) {
        return y;
      }
    }

    return Math.min(idealCutY, maxY);
  }

  const sourceCtx = canvas.getContext("2d");

  let srcY = 0;
  let pageIndex = 0;

  while (srcY < imgH) {
    if (pageIndex > 0) pdf.addPage();

    const idealCutY = Math.min(srcY + pxPerPage, imgH);
    const cutY = idealCutY >= imgH ? imgH : findSafeCut(sourceCtx, srcY, idealCutY, imgH, imgW);

    const srcH = cutY - srcY;

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = imgW;
    pageCanvas.height = srcH;

    const ctx = pageCanvas.getContext("2d");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

    ctx.drawImage(canvas, 0, srcY, imgW, srcH, 0, 0, imgW, srcH);

    const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.95);
    const pageMmH = (srcH / imgW) * A4_W;

    const yOffset = pageIndex === 0 ? 0 : TOP_MARGIN;

    pdf.addImage(pageImgData, "JPEG", 0, yOffset, A4_W, pageMmH);

    pdf.setFillColor(238, 243, 250);
    pdf.rect(0, A4_H - FOOTER_H, A4_W, FOOTER_H, "F");

    pdf.setFontSize(7);
    pdf.setTextColor(90, 110, 140);

    pdf.text(footerLeft, 8, A4_H - 6);
    pdf.text(footerCenter, A4_W / 2, A4_H - 6, { align: "center" });
    pdf.text(footerRight, A4_W - 8, A4_H - 6, { align: "right" });

    srcY = cutY;
    pageIndex++;
  }

  const datePart = typeof date === "string" ? date.slice(0, 10) : new Date(date || Date.now()).toISOString().slice(0, 10);

  const storeName = storeNameRaw.replace(/\s+/g, "-").toLowerCase();

  !sendToEmail && pdf.save(`daily-sales-${storeName}-${datePart}.pdf`);

  return pdf.output("blob");
} */
async function downloadPDF(transactions, computed, date, sendToEmail = false, currency = "₦") {
  const jsPDF = await loadJsPDF();

  const html = buildPDFHtml({ transactions, computed, date, currency });

  const iframe = document.createElement("iframe");
  iframe.style.cssText = ["position:fixed", "left:-9999px", "top:0", "width:794px", "height:10px", "border:none", "visibility:hidden"].join(";");

  document.body.appendChild(iframe);

  const customerSet = new Set(transactions.map((tx) => (tx.customer || {}).fullName).filter(Boolean));

  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  await new Promise((r) => setTimeout(r, 300));

  const iDoc = iframe.contentDocument;
  const iBody = iDoc.body;
  const iHtml = iDoc.documentElement;

  const fullH = Math.max(iBody.scrollHeight, iBody.offsetHeight, iHtml.scrollHeight, iHtml.offsetHeight);

  iframe.style.height = `${fullH}px`;

  await new Promise((r) => setTimeout(r, 800));

  const canvas = await window.html2canvas(iBody, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    width: 794,
    height: fullH,
    windowWidth: 794,
    windowHeight: fullH,
    backgroundColor: "#ffffff",
    imageTimeout: 3000,
  });

  document.body.removeChild(iframe);

  const A4_W = 210;
  const A4_H = 297;

  const FOOTER_H = 14;
  const BOTTOM_MARGIN = 8;
  const TOP_MARGIN = 8;
  const CONTENT_H = A4_H - FOOTER_H - BOTTOM_MARGIN - TOP_MARGIN;

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const imgW = canvas.width;
  const imgH = canvas.height;

  const storeNameRaw = ((transactions[0] || {}).store || {}).name || "store";

  const footerLeft = `StoreLense • ${storeNameRaw}`;
  const footerCenter = `${transactions.length} transactions • ${customerSet.size || 0} customer${customerSet.size !== 1 ? "s" : ""}`;

  const footerRight = new Date().toLocaleString();

  const pxPerPage = Math.floor((CONTENT_H / A4_W) * imgW);
  const minSliceH = Math.floor(pxPerPage * 0.65);

  function isMostlyWhiteRow(ctx, y, width) {
    const data = ctx.getImageData(0, y, width, 1).data;
    let white = 0;

    for (let x = 0; x < width; x++) {
      const i = x * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (r > 245 && g > 245 && b > 245) white++;
    }

    return white / width > 0.92;
  }

  function findSafeCut(ctx, startY, idealCutY, maxY, width) {
    const searchUpTo = 220; // pixels upward to search for whitespace

    for (let y = idealCutY; y > idealCutY - searchUpTo && y > startY + minSliceH; y--) {
      if (isMostlyWhiteRow(ctx, y, width)) {
        return y;
      }
    }

    return Math.min(idealCutY, maxY);
  }

  const sourceCtx = canvas.getContext("2d");

  let srcY = 0;
  let pageIndex = 0;

  while (srcY < imgH) {
    if (pageIndex > 0) pdf.addPage();

    const idealCutY = Math.min(srcY + pxPerPage, imgH);
    const cutY = idealCutY >= imgH ? imgH : findSafeCut(sourceCtx, srcY, idealCutY, imgH, imgW);

    const srcH = cutY - srcY;

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = imgW;
    pageCanvas.height = srcH;

    const ctx = pageCanvas.getContext("2d");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

    ctx.drawImage(canvas, 0, srcY, imgW, srcH, 0, 0, imgW, srcH);

    const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.95);
    const pageMmH = (srcH / imgW) * A4_W;

    const yOffset = pageIndex === 0 ? 0 : TOP_MARGIN;

    pdf.addImage(pageImgData, "JPEG", 0, yOffset, A4_W, pageMmH);

    pdf.setFillColor(238, 243, 250);
    pdf.rect(0, A4_H - FOOTER_H, A4_W, FOOTER_H, "F");

    pdf.setFontSize(7);
    pdf.setTextColor(90, 110, 140);

    pdf.text(footerLeft, 8, A4_H - 6);
    pdf.text(footerCenter, A4_W / 2, A4_H - 6, { align: "center" });
    pdf.text(footerRight, A4_W - 8, A4_H - 6, { align: "right" });

    srcY = cutY;
    pageIndex++;
  }

  const datePart = typeof date === "string" ? date.slice(0, 10) : new Date(date || Date.now()).toISOString().slice(0, 10);

  const storeName = storeNameRaw.replace(/\s+/g, "-").toLowerCase();
  const fileName = `daily-sales-${storeName}-${datePart}.pdf`;

  const pdfBlob = pdf.output("blob");

  // Callers that only need the blob (e.g. to attach to an email) get it
  // back here without anything being opened/downloaded on screen.
  if (sendToEmail) {
    return pdfBlob;
  }

  // pdf.save() triggers a direct download, which mobile browsers commonly
  // block or silently swallow — so instead of saving, open a preview.
  try {
    const dataUri = pdf.output("datauristring");
    const base64PDF = dataUri.split(",")[1];
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // NOTE: openPdfPreview(blob, base64) must be available in this
      // module (import it from wherever your app already defines it) —
      // it renders the PDF in-page instead of relying on a download.
      openPdfPreview(pdfBlob, base64PDF);
    } else {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>${fileName}</title>
              <style>
                body, html {
                  margin: 0;
                  padding: 0;
                  height: 100%;
                  overflow: hidden;
                }
                iframe {
                  width: 100%;
                  height: 100%;
                  border: none;
                }
              </style>
            </head>
            <body>
              <iframe src="${pdfUrl}" type="application/pdf" width="100%" height="100%"></iframe>
            </body>
          </html>
        `);
        newWindow.document.close();
      } else {
        // Popup blocked — fall back to a plain new tab.
        window.open(pdfUrl, "_blank");
      }
    }
  } catch (previewError) {
    console.error("Error opening PDF preview:", previewError);
    throw new Error(`Error creating or opening PDF: ${previewError.message}`);
  }

  return pdfBlob;
}
/* 
═══════════════════════════════════════════════
   PAGE CSS  (all dsc- prefixed)
═══════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.dsc-root*,.dsc-root *::before,.dsc-root *::after{box-sizing:border-box;margin:0;padding:0;}
.dsc-root{
  --dsc-bg:#f0f4fa;--dsc-card:#fff;--dsc-border:#e2e8f2;--dsc-text:#1a2236;
  --dsc-muted:#6b7a99;--dsc-blue2:#2563EB;--dsc-blue-dim:#dbeafe;
  --dsc-navy:#1e3a5f;--dsc-green:#16a34a;--dsc-green-dim:#dcfce7;
  --dsc-red:#dc2626;--dsc-red-dim:#fee2e2;--dsc-amber:#d97706;
  --dsc-amber-dim:#fef3c7;--dsc-teal:#0d9488;--dsc-teal-dim:#ccfbf1;
  --dsc-purple:#7c3aed;--dsc-purple-dim:#ede9fe;--dsc-radius:14px;
  --dsc-shadow:0 4px 28px rgba(30,58,95,.10);
  font-family:'Nunito',sans-serif;background:var(--dsc-bg);color:var(--dsc-text);
  min-height:100vh;padding:12px;-webkit-font-smoothing:antialiased;overflow-x:hidden;width:100%;
  position:absolute;top:0;left:0;display:flex;align-items:center;justify-content:center;
  z-index:52;
}

.dsc-modal{background:var(--dsc-card);border-radius:var(--dsc-radius);box-shadow:var(--dsc-shadow);max-width:980px;width:100%;margin:0 auto;overflow:hidden;animation:dscFadeUp .35s ease both;}
@keyframes dscFadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
.dsc-hdr{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid var(--dsc-border);gap:10px;flex-wrap:wrap;}
.dsc-hdr-title{font-size:clamp(13px,3.5vw,18px);font-weight:800;color:var(--dsc-navy);line-height:1.3;flex:1;min-width:0;}
.dsc-hdr-date{font-size:12px;font-weight:600;color:var(--dsc-muted);margin-left:10px;}
.dsc-btn-close{background:var(--dsc-red);color:#fff;border:none;border-radius:8px;padding:7px 14px;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;flex-shrink:0;white-space:nowrap;transition:opacity .2s;}
.dsc-btn-close:hover{opacity:.82;}
/* Stats strip */
.dsc-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(90px,1fr));border-bottom:2px solid var(--dsc-border);background:#f7f9fd;}
.dsc-stat{padding:12px 10px;text-align:center;border-right:1px solid var(--dsc-border);}
.dsc-stat:last-child{border-right:none;}
.dsc-stat-label{font-size:9px;font-weight:800;letter-spacing:.8px;text-transform:uppercase;color:var(--dsc-muted);}
.dsc-stat-val{font-size:clamp(13px,3vw,19px);font-weight:800;margin-top:4px;}
/* Body */
.dsc-body{padding:14px 18px;display:flex;flex-direction:column;gap:16px;}
.dsc-slabel{font-size:10px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:var(--dsc-muted);margin-bottom:9px;}
/* Pills */
.dsc-pills{display:flex;flex-wrap:wrap;gap:7px;}
.dsc-pill{display:flex;align-items:center;gap:6px;background:var(--dsc-bg);border:1px solid var(--dsc-border);border-radius:20px;padding:4px 11px;font-size:12px;font-weight:600;}
.dsc-pill-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
/* Tables */
.dsc-table-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch;border-radius:10px;border:1px solid var(--dsc-border);}
.dsc-table{width:100%;border-collapse:collapse;}
.dsc-table thead th{background:var(--dsc-navy);color:#fff;font-size:9.5px;font-weight:800;letter-spacing:.5px;text-transform:uppercase;padding:9px 11px;text-align:right;white-space:nowrap;}
.dsc-table thead th:first-child,.dsc-table thead th.dsc-th-left{text-align:left;}
.dsc-table tbody tr{border-top:1px solid var(--dsc-border);transition:background .15s;}
.dsc-table tbody tr:hover{background:#f7f9ff;}
.dsc-table tbody tr.dsc-tr-alt{background:#f5f8ff;}
.dsc-table tbody tr.dsc-tr-returned{opacity:.48;}
.dsc-table tbody tr.dsc-tr-returned .dsc-cell-name-text{text-decoration:line-through;}
.dsc-table td{padding:9px 11px;text-align:right;font-size:12px;font-weight:600;white-space:nowrap;vertical-align:middle;}
.dsc-table td:first-child,.dsc-table td.dsc-td-left{text-align:left;}
.dsc-cell-name{display:flex;align-items:center;gap:6px;}
.dsc-cell-img{width:26px;height:26px;border-radius:5px;object-fit:cover;border:1px solid var(--dsc-border);flex-shrink:0;}
.dsc-cell-name-text{font-weight:700;font-size:12.5px;}
/* Mobile tx cards */
.dsc-tx-cards{display:none;flex-direction:column;gap:8px;}
.dsc-tx-card{background:#f7f9fd;border:1px solid var(--dsc-border);border-radius:10px;padding:12px 14px;}
.dsc-tx-card-hdr{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;margin-bottom:8px;}
.dsc-tx-card-name{font-size:13px;font-weight:800;color:var(--dsc-navy);}
.dsc-tx-card-date{font-size:11px;color:var(--dsc-muted);font-weight:600;margin-top:2px;}
.dsc-tx-card-body{display:grid;grid-template-columns:1fr 1fr;gap:6px 10px;}
.dsc-sc-row{display:flex;flex-direction:column;gap:1px;}
.dsc-sc-label{font-size:10px;font-weight:700;color:var(--dsc-muted);text-transform:uppercase;letter-spacing:.5px;}
.dsc-sc-val{font-size:13px;font-weight:700;color:var(--dsc-text);}
/* Mobile item cards */
.dsc-item-cards{display:none;flex-direction:column;gap:8px;}
.dsc-item-card{background:#f7f9fd;border:1px solid var(--dsc-border);border-radius:10px;padding:12px 14px;}
.dsc-item-card-hdr{display:flex;align-items:center;gap:10px;margin-bottom:8px;}
.dsc-item-card-img{width:34px;height:34px;border-radius:7px;object-fit:cover;border:1px solid var(--dsc-border);flex-shrink:0;}
.dsc-item-card-name{font-size:13px;font-weight:800;color:var(--dsc-navy);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.dsc-item-card-cat{font-size:11px;color:var(--dsc-muted);font-weight:600;}
.dsc-item-card-body{display:grid;grid-template-columns:1fr 1fr;gap:6px 10px;}
/* Badges — per-type color/background now comes from PAYMENT_TYPE_STYLE /
   NON_PAYMENT_BADGE_STYLE via inline style, so only the shared shape lives here. */
.dsc-badge{display:inline-flex;align-items:center;border-radius:6px;padding:2px 8px;font-size:11px;font-weight:700;white-space:nowrap;}
/* Payment breakdown: clickable badge + breakdown */
.dsc-payment-toggle{display:inline-flex;align-items:center;gap:4px;background:none;border:none;padding:0;cursor:pointer;font-family:inherit;}
.dsc-payment-toggle svg{color:var(--dsc-muted);transition:transform .15s ease;flex-shrink:0;}
.dsc-payment-toggle.dsc-payment-open svg{transform:rotate(180deg);}
.dsc-payment-breakdown{display:flex;flex-direction:column;gap:6px;padding:4px 2px;}
.dsc-payment-row{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid var(--dsc-border);border-radius:8px;padding:7px 10px;}
.dsc-payment-row-info{flex:1;display:flex;flex-direction:column;gap:1px;min-width:0;text-align:left;}
.dsc-payment-row-name{font-size:12px;font-weight:700;color:var(--dsc-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.dsc-payment-row-ref{font-size:10.5px;color:var(--dsc-muted);font-weight:600;}
.dsc-payment-row-amount{font-size:12.5px;font-weight:800;color:var(--dsc-navy);flex-shrink:0;white-space:nowrap;}
.dsc-payment-breakdown-mobile{margin-top:8px;padding-top:8px;border-top:1px dashed var(--dsc-border);}
.dsc-payment-breakdown-cell{background:#f7f9fd!important;text-align:left!important;white-space:normal!important;padding:10px 11px!important;}
/* IQ card */
.dsc-iq-card{background:#f7f9fd;border:1px solid var(--dsc-border);border-radius:var(--dsc-radius);padding:14px;}
.dsc-iq-title{font-size:10px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:var(--dsc-muted);text-align:center;margin-bottom:12px;}
.dsc-iq-items{display:flex;flex-direction:row;flex-wrap:wrap;gap:8px;}
.dsc-iq-row{flex:1;min-width:130px;display:flex;align-items:center;justify-content:space-between;gap:8px;background:var(--dsc-card);border:1px solid var(--dsc-border);border-radius:10px;padding:10px 12px;}
.dsc-iq-info{flex:1;min-width:0;}
.dsc-iq-name{font-size:12.5px;font-weight:700;color:var(--dsc-navy);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.dsc-iq-sub{font-size:10.5px;color:var(--dsc-muted);font-weight:600;margin-top:2px;}
.dsc-iq-circle{width:40px;height:40px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:17px;font-weight:800;}
.dsc-iq-blue{background:var(--dsc-blue-dim);color:var(--dsc-blue2);}
.dsc-iq-teal{background:var(--dsc-teal-dim);color:var(--dsc-teal);}
.dsc-iq-amber{background:var(--dsc-amber-dim);color:var(--dsc-amber);}
.dsc-iq-purple{background:var(--dsc-purple-dim);color:var(--dsc-purple);}
.dsc-iq-total{margin-top:10px;background:var(--dsc-navy);border-radius:10px;padding:10px 14px;display:flex;align-items:center;justify-content:space-between;}
.dsc-iq-total-label{font-size:10px;font-weight:700;color:rgba(255,255,255,.6);letter-spacing:.6px;text-transform:uppercase;}
.dsc-iq-total-val{font-size:20px;font-weight:800;color:#fff;}
/* Bars */
.dsc-bar-section{display:flex;flex-direction:column;gap:6px;margin-top:12px;}
.dsc-bar-row{display:flex;align-items:center;gap:7px;}
.dsc-bar-label{width:54px;color:var(--dsc-muted);font-weight:700;font-size:10.5px;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.dsc-bar-track{flex:1;background:var(--dsc-border);border-radius:99px;height:6px;overflow:hidden;}
.dsc-bar-fill{height:100%;border-radius:99px;width:0%;transition:width 1s cubic-bezier(.4,0,.2,1);}
.dsc-bar-pct{width:30px;text-align:right;font-weight:800;font-size:10.5px;color:var(--dsc-muted);flex-shrink:0;}
/* Bottom grid */
.dsc-bot-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
.dsc-inc-card,.dsc-rev-card{background:#f7f9fd;border:1px solid var(--dsc-border);border-radius:var(--dsc-radius);padding:14px;}
.dsc-inc-row{display:flex;justify-content:space-between;align-items:flex-start;padding:8px 0;border-bottom:1px solid var(--dsc-border);gap:8px;}
.dsc-inc-row:last-child{border-bottom:none;}
.dsc-inc-lbl{color:var(--dsc-muted);font-weight:600;font-size:12px;line-height:1.4;flex:1;}
.dsc-inc-val{font-weight:800;font-size:13px;white-space:nowrap;flex-shrink:0;}
.dsc-inc-amber{color:var(--dsc-amber);}.dsc-inc-red{color:var(--dsc-red);}.dsc-inc-purple{color:var(--dsc-purple);}
.dsc-lbl-amber{color:var(--dsc-amber);}.dsc-lbl-red{color:var(--dsc-red);}.dsc-lbl-purple{color:var(--dsc-purple);}
.dsc-total-box{margin-top:12px;background:var(--dsc-navy);border-radius:10px;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:4px;}
.dsc-total-lbl{font-size:10px;font-weight:700;letter-spacing:.7px;text-transform:uppercase;color:rgba(255,255,255,.6);}
.dsc-total-val{font-size:clamp(16px,4vw,21px);font-weight:800;color:#fff;}
.dsc-rev-methods{display:flex;flex-direction:column;gap:8px;}
.dsc-rv{background:var(--dsc-card);border:1px solid var(--dsc-border);border-radius:10px;padding:10px 12px;display:flex;justify-content:space-between;align-items:center;}
.dsc-rv-lbl{font-size:10px;font-weight:800;color:var(--dsc-muted);text-transform:uppercase;letter-spacing:.5px;}
.dsc-rv-val{font-size:14px;font-weight:800;}
/* Footer */
.dsc-footer{display:flex;justify-content:center;gap:12px;padding:14px 18px 20px;flex-wrap:wrap;border-top:1px solid var(--dsc-border);}
.dsc-btn{padding:10px 24px;border-radius:9px;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;border:none;transition:all .2s;display:flex;align-items:center;gap:7px;}
.dsc-btn:disabled{opacity:.5;cursor:not-allowed;}
.dsc-btn-outline{background:#fff;border:2px solid var(--dsc-border);color:var(--dsc-text);}
.dsc-btn-outline:hover:not(:disabled){border-color:var(--dsc-blue2);color:var(--dsc-blue2);}
.dsc-btn-primary{background:var(--dsc-blue2);color:#fff;}
.dsc-btn-primary:hover{background:#1d4ed8;}
/* PDF overlay */
.dsc-pdf-overlay{position:fixed;inset:0;background:rgba(30,58,95,.65);z-index:9999;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:14px;}
.dsc-spinner{width:44px;height:44px;border-radius:50%;border:4px solid rgba(255,255,255,.2);border-top-color:#fff;animation:dscSpin .75s linear infinite;}
@keyframes dscSpin{to{transform:rotate(360deg);}}
.dsc-pdf-overlay p{color:#fff;font-family:'Nunito',sans-serif;font-weight:700;font-size:14px;}
/* Responsive */
@media(max-width:640px){
  .dsc-table-wrap{display:none!important;}
  .dsc-tx-cards{display:flex!important;}
  .dsc-item-cards{display:flex!important;}
}
@media(max-width:480px){
  .dsc-bot-grid{grid-template-columns:1fr;}
  .dsc-iq-items{flex-direction:column;}
  .dsc-iq-row{min-width:unset;}
  .dsc-footer{flex-direction:column;align-items:stretch;}
  .dsc-btn{justify-content:center;}
  .dsc-body{padding:14px 8px;}
  .dsc-root{padding:10px 2px}
}
`;

/* ─── Sub-components ─────────────────────────── */
function Badge({ type }) {
  const style = resolveBadgeStyle(type);
  return (
    <span className='dsc-badge' style={{ color: style.color, background: style.bg }}>
      {style.label}
    </span>
  );
}

function AnimatedBar({ color, pct, label }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current) ref.current.style.width = pct + "%";
    }, 300);
    return () => clearTimeout(t);
  }, [pct]);
  return (
    <div className='dsc-bar-row'>
      <div className='dsc-bar-label' title={label}>
        {label}
      </div>
      <div className='dsc-bar-track'>
        <div ref={ref} className='dsc-bar-fill' style={{ background: color }} />
      </div>
      <div className='dsc-bar-pct'>{Math.round(pct)}%</div>
    </div>
  );
}

const IQ_COLORS = ["dsc-iq-blue", "dsc-iq-teal", "dsc-iq-amber", "dsc-iq-purple"];

function ChevronDownIcon() {
  return (
    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
      <polyline points='6 9 12 15 18 9'></polyline>
    </svg>
  );
}

/* Renders a transaction's `transactionDetails.payments` array — used both
   under the desktop table's expanded row and in the mobile card. Works
   for a single-entry array (non-split: shows the one account used) or
   a multi-entry array (split: shows the full breakdown). */
function PaymentBreakdown({ payments = [], currency }) {
  return (
    <div className='dsc-payment-breakdown'>
      {payments.map((p) => (
        <div className='dsc-payment-row' key={p._id || `${p.paymentType}-${p.amount}`}>
          <Badge type={p.paymentType} />
          <div className='dsc-payment-row-info'>
            <span className='dsc-payment-row-name'>{p.receivingAccountName || "—"}</span>
            {p.reference && <span className='dsc-payment-row-ref'>Ref: {p.reference}</span>}
          </div>
          <span className='dsc-payment-row-amount'>
            {currency}
            {fmt(p.amount)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */

export function formatToKMB(amount, currency = "₦") {
  if (amount == null || isNaN(amount)) return `${currency} 0`;

  const abs = Math.abs(amount);

  let value, suffix;

  if (abs >= 1_000_000_000) {
    value = amount / 1_000_000_000;
    suffix = "B";
  } else if (abs >= 1_000_000) {
    value = amount / 1_000_000;
    suffix = "M";
  } else if (abs >= 1_000) {
    value = amount / 1_000;
    suffix = "K";
  } else {
    return `${currency} ${amount.toLocaleString()}`;
  }

  return `${currency} ${value.toFixed(2).replace(/\.00$/, "")}${suffix}`;
}

export default function DailySalesCheck({ transactions = [], computed = {}, date, onClose, onEmail }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  // Which transactions currently have their payment/account breakdown
  // open, keyed by the same key used for the table row (transID + idx).
  const [expandedPayments, setExpandedPayments] = useState(() => new Set());

  const togglePayments = (key) => {
    setExpandedPayments((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ── Destructure computed (from salesCheckFunction) ── */
  const {
    listOfItems = [],
    eachItemAndQuantity = [],
    incomeBreakdown = { incomeFromSoldProducts: 0, amountOnLoan: 0 },
    totalDiscount = 0,
    totalVAT = 0,
  } = computed;

  /* ── Derived values ── */
  const currency = (transactions[0] || {}).currency || "₦";

  console.log(currency, transactions[1]);

  const storeName = ((transactions[0] || {}).store || {}).name || "Store";
  const txSummaries = buildTxSummaries(transactions);
  const totalItemQty = eachItemAndQuantity.reduce((s, i) => s + (i.quantity || 0), 0);
  // Built from each transaction's own payments (decomposing splits) rather
  // than trusting computed.revenueDistribution, which only ever tracked
  // cash/card/mobilePay and would silently drop POS/bankTransfer/etc.
  const revenueDistribution = buildRevenueDistribution(txSummaries);
  const revTotal = Object.values(revenueDistribution).reduce((s, v) => s + v, 0);
  const grandTotal =
    incomeBreakdown.incomeFromSoldProducts +
    /* Number(totalVAT || 0) + */
    Number(incomeBreakdown.clearedLoan || 0) -
    /*  Number(totalDiscount || 0) - */
    Number(incomeBreakdown.expenses || 0);
  const customerSet = new Set(transactions.map((tx) => (tx.customer || {}).fullName).filter(Boolean));

  const returnedCount = listOfItems.filter((i) => i.returned || i.productIsNewlyReturned).length;
  const loanCount = listOfItems.filter((i) => i.transactionType === "loan").length;
  const suppliedCount = listOfItems.filter((i) => i.supplied && !i.returned).length;
  const notSuppliedCount = listOfItems.filter((i) => i.supplied === false).length;

  const revBars = PAYMENT_TYPE_ORDER.filter((k) => revenueDistribution[k] > 0).map((k) => ({
    key: k,
    label: PAYMENT_TYPE_STYLE[k].label,
    color: PAYMENT_TYPE_STYLE[k].color,
    pct: revTotal ? (revenueDistribution[k] / revTotal) * 100 : 0,
  }));
  const iqBars = eachItemAndQuantity.map((item, idx) => ({
    label: item.name,
    pct: totalItemQty ? (item.quantity / totalItemQty) * 100 : 0,
    color: ["var(--dsc-blue2)", "var(--dsc-teal)", "var(--dsc-amber)", "var(--dsc-purple)"][idx % 4],
  }));

  async function handleDownload() {
    setPdfLoading(true);
    try {
      await downloadPDF(transactions, computed, date);
    } catch (err) {
      console.error(err);
      alert("PDF generation failed. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  }

  const handleSendToEmail = async () => {
    setPdfLoading(true);
    try {
      const pdfBlob = await downloadPDF(transactions, computed, date, true);
      await onEmail(pdfBlob);
    } catch (error) {
      console.error("Email sending failed:", error);
      uiAlert("Failed to send email. Please try again.", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  /* ── Render ── */
  return (
    <>
      <style>{CSS}</style>

      {pdfLoading && (
        <div className='dsc-pdf-overlay'>
          <div className='dsc-spinner' />
          <p>Generating PDF…</p>
        </div>
      )}

      <div className='dsc-root'>
        <div className='dsc-modal'>
          {/* HEADER */}
          <div className='dsc-hdr'>
            <h1 className='dsc-hdr-title'>
              Daily Sales Check — {storeName}
              <span className='dsc-hdr-date'>{fmtDateShort(new Date(date || new Date()).toISOString())}</span>
            </h1>
            <button className='dsc-btn-close' onClick={onClose}>
              Close
            </button>
          </div>

          {/* STATS STRIP */}
          <div className='dsc-stats'>
            {[
              { label: "Transactions", val: transactions.length, color: "var(--dsc-blue2)" },
              { label: "Customers", val: customerSet.size, color: "var(--dsc-teal)" },
              { label: "Total Items", val: totalItemQty, color: "var(--dsc-amber)" },
              { label: "Grand Total", val: `${formatToKMB(grandTotal, currency)}`, color: "var(--dsc-navy)" },
              { label: "On Loan", val: `${currency} ${fmt(incomeBreakdown.amountOnLoan || 0)}`, color: "var(--dsc-amber)" },
              ...(totalVAT > 0 ? [{ label: "VAT", val: ` ${formatToKMB(totalVAT, currency)}`, color: "var(--dsc-purple)" }] : []),
            ].map(({ label, val, color }) => (
              <div className='dsc-stat' key={label}>
                <div className='dsc-stat-label'>{label}</div>
                <div className='dsc-stat-val' style={{ color }}>
                  {val}
                </div>
              </div>
            ))}
          </div>

          <div className='dsc-body'>
            {/* PILLS */}
            <div className='dsc-pills'>
              <div className='dsc-pill'>
                <div className='dsc-pill-dot' style={{ background: "var(--dsc-blue2)" }} />
                {listOfItems.length} line items
              </div>
              <div className='dsc-pill'>
                <div className='dsc-pill-dot' style={{ background: "var(--dsc-navy)" }} />
                {totalItemQty} total qty
              </div>
              {loanCount > 0 && (
                <div className='dsc-pill'>
                  <div className='dsc-pill-dot' style={{ background: "var(--dsc-amber)" }} />
                  {loanCount} on loan
                </div>
              )}
              {returnedCount > 0 && (
                <div className='dsc-pill' style={{ background: "var(--dsc-red-dim)", borderColor: "#fecaca" }}>
                  <div className='dsc-pill-dot' style={{ background: "var(--dsc-red)" }} />
                  {returnedCount} returned
                </div>
              )}
              {suppliedCount > 0 && (
                <div className='dsc-pill' style={{ background: "var(--dsc-purple-dim)", borderColor: "#ddd6fe" }}>
                  <div className='dsc-pill-dot' style={{ background: "var(--dsc-purple)" }} />
                  {suppliedCount} supplied
                </div>
              )}

              {notSuppliedCount > 0 && (
                <div className='dsc-pill' style={{ background: "#fef3c7", borderColor: "#fde68a" }}>
                  <div className='dsc-pill-dot' style={{ background: "var(--dsc-amber)" }} />
                  {notSuppliedCount} not supplied
                </div>
              )}
            </div>

            {/* ── TRANSACTIONS SUMMARY ── */}
            <div>
              <div className='dsc-slabel'>Transactions Summary</div>

              {/* Desktop table */}
              <div className='dsc-table-wrap'>
                <table className='dsc-table'>
                  <thead>
                    <tr>
                      <th className='dsc-th-left'>Customer</th>
                      <th className='dsc-th-left'>Date & Time</th>
                      <th>Sale Type</th>
                      <th>Payment</th>
                      <th>Items</th>
                      <th>Sub-total ({currency})</th>
                      <th>On Loan ({currency})</th>
                      <th className='dsc-th-left'>Sold by</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txSummaries.map((tx, idx) => {
                      const isRet = tx.deliveryStatus === "returned";
                      const rowKey = tx.transID + idx;
                      const hasPayments = tx.payments && tx.payments.length > 0;
                      const isExpanded = expandedPayments.has(rowKey);
                      return (
                        <Fragment key={rowKey}>
                          <tr className={[idx % 2 === 1 ? "dsc-tr-alt" : "", isRet ? "dsc-tr-returned" : ""].filter(Boolean).join(" ")}>
                            <td className='dsc-td-left' style={{ fontWeight: 700 }}>
                              {tx.customer}
                            </td>
                            <td className='dsc-td-left' style={{ color: "var(--dsc-muted)", fontWeight: 600, fontSize: 11 }}>
                              {fmtDate(tx.date)}
                            </td>
                            <td>
                              <Badge type={tx.operationType} />
                            </td>
                            <td>
                              {hasPayments ? (
                                <button
                                  type='button'
                                  className={`dsc-payment-toggle${isExpanded ? " dsc-payment-open" : ""}`}
                                  onClick={() => togglePayments(rowKey)}
                                  aria-expanded={isExpanded}
                                >
                                  <Badge type={tx.paymentMode} />
                                  <ChevronDownIcon />
                                </button>
                              ) : (
                                <Badge type={tx.paymentMode} />
                              )}
                            </td>
                            <td>{tx.itemCount}</td>
                            <td style={{ fontWeight: 700 }}>{fmt(tx.subTotal)}</td>
                            <td style={{ color: tx.onLoan > 0 ? "var(--dsc-amber)" : "var(--dsc-muted)", fontWeight: tx.onLoan > 0 ? 700 : 400 }}>
                              {tx.onLoan > 0 ? fmt(tx.onLoan) : "—"}
                            </td>
                            <td className='dsc-td-left'>{tx.soldBy}</td>
                            <td>{isRet ? <Badge type='returned' /> : <span style={{ color: "var(--dsc-muted)", fontSize: 11 }}>—</span>}</td>
                          </tr>
                          {hasPayments && isExpanded && (
                            <tr>
                              <td className='dsc-payment-breakdown-cell' colSpan={9}>
                                <PaymentBreakdown payments={tx.payments} currency={currency} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile tx cards */}
              <div className='dsc-tx-cards'>
                {txSummaries.map((tx, idx) => {
                  const isRet = tx.deliveryStatus === "returned";
                  const rowKey = tx.transID + idx;
                  const hasPayments = tx.payments && tx.payments.length > 0;
                  const isExpanded = expandedPayments.has(rowKey);
                  return (
                    <div
                      key={rowKey}
                      className='dsc-tx-card'
                      style={{
                        borderLeft: `3px solid ${isRet ? "var(--dsc-red)" : tx.operationType === "loan" ? "var(--dsc-amber)" : "var(--dsc-green)"}`,
                        opacity: isRet ? 0.6 : 1,
                      }}
                    >
                      <div className='dsc-tx-card-hdr'>
                        <div>
                          <div className='dsc-tx-card-name'>{tx.customer}</div>
                          <div className='dsc-tx-card-date'>{fmtDate(tx.date)}</div>
                        </div>
                        <div style={{ display: "flex", gap: 4, flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                          <Badge type={tx.operationType} />
                          {hasPayments ? (
                            <button
                              type='button'
                              className={`dsc-payment-toggle${isExpanded ? " dsc-payment-open" : ""}`}
                              onClick={() => togglePayments(rowKey)}
                              aria-expanded={isExpanded}
                            >
                              <Badge type={tx.paymentMode} />
                              <ChevronDownIcon />
                            </button>
                          ) : (
                            <Badge type={tx.paymentMode} />
                          )}
                          {isRet && <Badge type='returned' />}
                        </div>
                      </div>
                      <div className='dsc-tx-card-body'>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Items</span>
                          <span className='dsc-sc-val'>{tx.itemCount}</span>
                        </div>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Sub-total</span>
                          <span className='dsc-sc-val'>
                            {currency}
                            {fmt(tx.subTotal)}
                          </span>
                        </div>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>On Loan</span>
                          <span className='dsc-sc-val' style={{ color: tx.onLoan > 0 ? "var(--dsc-amber)" : "inherit" }}>
                            {tx.onLoan > 0 ? `${currency}${fmt(tx.onLoan)}` : "—"}
                          </span>
                        </div>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Sold by</span>
                          <span className='dsc-sc-val'>{tx.soldBy}</span>
                        </div>
                      </div>
                      {hasPayments && isExpanded && (
                        <div className='dsc-payment-breakdown-mobile'>
                          <PaymentBreakdown payments={tx.payments} currency={currency} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── ALL ITEMS SOLD ── */}
            <div>
              <div className='dsc-slabel'>All Items Sold</div>

              {/* Desktop table */}
              <div className='dsc-table-wrap'>
                <table className='dsc-table'>
                  <thead>
                    <tr>
                      <th className='dsc-th-left'>Item</th>
                      <th>Qty</th>
                      <th>Unit ({currency})</th>
                      <th>Total ({currency})</th>
                      <th>Type</th>
                      <th>Paid ({currency})</th>
                      <th>Balance {currency}</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listOfItems.map((item, idx) => {
                      const isRet = item.returned || item.productIsNewlyReturned;
                      const isSupp = item.supplied;
                      return (
                        <tr
                          key={(item._id || item.id || "") + idx}
                          className={[idx % 2 === 1 ? "dsc-tr-alt" : "", isRet ? "dsc-tr-returned" : ""].filter(Boolean).join(" ")}
                        >
                          <td>
                            <div className='dsc-cell-name'>
                              {item.productImage && (
                                <img
                                  src={item.productImage}
                                  alt={item.name}
                                  className='dsc-cell-img'
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}
                              <span className='dsc-cell-name-text'>{item.name}</span>
                            </div>
                          </td>
                          <td>{item.quantity}</td>
                          <td>{fmt(item.soldUnitPrice)}</td>
                          <td style={{ fontWeight: 700 }}>{fmt(item.totalAmount)}</td>
                          <td>
                            <Badge type={item.transactionType || "cash"} />
                          </td>
                          <td style={item.paid > 0 && !isRet ? { color: "var(--dsc-green)" } : {}}>{fmt(item.paid)}</td>
                          <td style={item.balance > 0 ? { color: "var(--dsc-amber)", fontWeight: 800 } : {}}>{fmt(item.balance)}</td>
                          <td>
                            <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              {isRet && <Badge type='returned' />}
                              {isSupp && <Badge type='supplied' />}
                              {!isRet && !isSupp && (
                                <span style={{ color: "var(--dsc-muted)", fontSize: 11 }}>
                                  <Badge type={"notSupplied" || "cash"} />
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile item cards */}
              <div className='dsc-item-cards'>
                {listOfItems.map((item, idx) => {
                  const isRet = item.returned || item.productIsNewlyReturned;
                  const isSupp = item.supplied;
                  return (
                    <div
                      key={(item._id || item.id || "") + idx}
                      className='dsc-item-card'
                      style={{
                        borderLeft: `3px solid ${
                          isRet ? "var(--dsc-red)" : item.transactionType === "loan" ? "var(--dsc-amber)" : isSupp ? "var(--dsc-purple)" : "var(--dsc-green)"
                        }`,
                        opacity: isRet ? 0.58 : 1,
                      }}
                    >
                      <div className='dsc-item-card-hdr'>
                        {item.productImage && (
                          <img
                            src={item.productImage}
                            alt={item.name}
                            className='dsc-item-card-img'
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className='dsc-item-card-name' title={item.name}>
                            {item.name}
                          </div>
                          <div className='dsc-item-card-cat'>{item.category}</div>
                        </div>
                        <div style={{ display: "flex", gap: 4, flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                          <Badge type={item.transactionType || "cash"} />
                          {isRet && <Badge type='returned' />}
                          {isSupp && <Badge type='supplied' />}
                          {!isRet && !isSupp && (
                            <span style={{ color: "var(--dsc-muted)", fontSize: 11 }}>
                              <Badge type={"notSupplied" || "cash"} />
                            </span>
                          )}
                        </div>
                      </div>
                      <div className='dsc-item-card-body'>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Qty</span>
                          <span className='dsc-sc-val'>
                            {item.quantity} {item.unit || ""}
                          </span>
                        </div>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Unit price</span>
                          <span className='dsc-sc-val'>
                            {currency}
                            {fmt(item.soldUnitPrice)}
                          </span>
                        </div>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Total</span>
                          <span className='dsc-sc-val'>
                            {currency}
                            {fmt(item.totalAmount)}
                          </span>
                        </div>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Paid</span>
                          <span className='dsc-sc-val' style={{ color: item.paid > 0 ? "var(--dsc-green)" : "inherit" }}>
                            {currency}
                            {fmt(item.paid)}
                          </span>
                        </div>
                        <div className='dsc-sc-row'>
                          <span className='dsc-sc-label'>Balance</span>
                          <span className='dsc-sc-val' style={{ color: item.balance > 0 ? "var(--dsc-amber)" : "inherit" }}>
                            {currency}
                            {fmt(item.balance)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── EACH ITEM & QTY ── */}
            {eachItemAndQuantity.length > 0 && (
              <div className='dsc-iq-card'>
                <div className='dsc-iq-title'>Each Item and Sold Quantity</div>
                <div className='dsc-iq-items'>
                  {eachItemAndQuantity.map((item, idx) => (
                    <div className='dsc-iq-row' key={item.productID || item.name + idx}>
                      <div className='dsc-iq-info'>
                        <div className='dsc-iq-name' title={item.name}>
                          {item.name}
                        </div>
                        <div className='dsc-iq-sub'>
                          {item.quantity} unit{item.quantity !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className={`dsc-iq-circle ${IQ_COLORS[idx % IQ_COLORS.length]}`}>{item.quantity}</div>
                    </div>
                  ))}
                </div>
                <div className='dsc-iq-total'>
                  <span className='dsc-iq-total-label'>Total Qty</span>
                  <span className='dsc-iq-total-val'>{totalItemQty}</span>
                </div>
                <div className='dsc-bar-section'>
                  {iqBars.map((b) => (
                    <AnimatedBar key={b.label} label={b.label} pct={b.pct} color={b.color} />
                  ))}
                </div>
              </div>
            )}

            {/* ── INCOME + REVENUE ── */}
            <div className='dsc-bot-grid'>
              <div className='dsc-inc-card'>
                <div className='dsc-slabel'>Income Breakdown ({currency})</div>
                <div className='dsc-inc-row'>
                  <span className='dsc-inc-lbl'>Income from sold products</span>
                  <span className='dsc-inc-val'>{fmt(incomeBreakdown.incomeFromSoldProducts)}</span>
                </div>

                {(incomeBreakdown.amountOnLoan || 0) > 0 && (
                  <div className='dsc-inc-row'>
                    <span className='dsc-inc-lbl dsc-lbl-amber'>Amount on loan</span>
                    <span className='dsc-inc-val dsc-inc-amber'>{fmt(incomeBreakdown.amountOnLoan)}</span>
                  </div>
                )}
                {(incomeBreakdown?.clearedLoan || 0) > 0 && (
                  <div className='dsc-inc-row'>
                    <span className='dsc-inc-lbl dsc-lbl-amber' style={{ color: "#0d9488" }}>
                      Amount from cleared credit
                    </span>
                    <span className='dsc-inc-val dsc-inc-amber' style={{ color: "#0d9488" }}>
                      {fmt(incomeBreakdown.clearedLoan)}
                    </span>
                  </div>
                )}
                {(incomeBreakdown?.expenses || 0) > 0 && (
                  <div className='dsc-inc-row'>
                    <span className='dsc-inc-lbl dsc-lbl-amber' style={{ color: "#940d32" }}>
                      Expenses
                    </span>
                    <span className='dsc-inc-val dsc-inc-amber' style={{ color: "#940d32" }}>
                      -{fmt(incomeBreakdown.expenses)}
                    </span>
                  </div>
                )}

                {totalDiscount > 0 && (
                  <div className='dsc-inc-row'>
                    <span className='dsc-inc-lbl dsc-lbl-red'>Total discount</span>
                    <span className='dsc-inc-val dsc-inc-red'>-{fmt(totalDiscount)}</span>
                  </div>
                )}

                {totalVAT > 0 && (
                  <div className='dsc-inc-row'>
                    <span className='dsc-inc-lbl dsc-lbl-purple'>VAT</span>
                    <span className='dsc-inc-val dsc-inc-purple'>+{fmt(totalVAT)}</span>
                  </div>
                )}
                <div className='dsc-total-box'>
                  <span className='dsc-total-lbl'>Grand Total</span>
                  <span className='dsc-total-val'>
                    {currency} {fmt(grandTotal)}
                  </span>
                </div>
              </div>

              <div className='dsc-rev-card'>
                <div className='dsc-slabel'>Revenue Distribution ({currency})</div>
                <div className='dsc-rev-methods'>
                  {PAYMENT_TYPE_ORDER.filter((k) => revenueDistribution[k] > 0).map((k) => (
                    <div className='dsc-rv' style={{ borderLeft: `4px solid ${PAYMENT_TYPE_STYLE[k].color}` }} key={k}>
                      <span className='dsc-rv-lbl'>{PAYMENT_TYPE_STYLE[k].label}</span>
                      <span className='dsc-rv-val' style={{ color: PAYMENT_TYPE_STYLE[k].color }}>
                        {fmt(revenueDistribution[k])}
                      </span>
                    </div>
                  ))}
                </div>
                <div className='dsc-bar-section'>
                  {revBars.map((b) => (
                    <AnimatedBar key={b.key} label={b.label} pct={b.pct} color={b.color} />
                  ))}
                </div>
                {(totalVAT > 0 || totalDiscount > 0) && (
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                    {totalVAT > 0 && (
                      <div className='dsc-rv' style={{ borderLeft: "4px solid var(--dsc-purple)" }}>
                        <span className='dsc-rv-lbl'>VAT</span>
                        <span className='dsc-rv-val' style={{ color: "var(--dsc-purple)" }}>
                          +{fmt(totalVAT)}
                        </span>
                      </div>
                    )}
                    {totalDiscount > 0 && (
                      <div className='dsc-rv' style={{ borderLeft: "4px solid var(--dsc-red)" }}>
                        <span className='dsc-rv-lbl'>Discount</span>
                        <span className='dsc-rv-val' style={{ color: "var(--dsc-red)" }}>
                          -{fmt(totalDiscount)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className='dsc-footer'>
            <button className='dsc-btn dsc-btn-outline' disabled={pdfLoading} onClick={handleDownload}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='7 10 12 15 17 10' />
                <line x1='12' y1='15' x2='12' y2='3' />
              </svg>
              {pdfLoading ? "Generating…" : "Download"}
            </button>
            <button className='dsc-btn dsc-btn-primary' onClick={handleSendToEmail} disabled={pdfLoading}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
                <polyline points='22,6 12,13 2,6' />
              </svg>
              Send to email
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   createMultiPagePDF
   Matches the DailySalesCheck UI:
     • Navy header  + date
     • Stats strip
     • Transactions summary table
     • All items sold table
     • Each item & quantity circles
     • Income breakdown  +  Revenue distribution
     • Footer with page numbers

   Usage (same call-site as before):
     const doc = await createMultiPagePDF(
       salesData, incomeBreakdown, listOfItems,
       revenueDistribution, eachItemAndQuantity,
       store.storeName, currenyCode, discount, totalVAT,
       transactions          // ← pass todaysTransaction array
     );
     doc.save(`${store.storeName}-sales-report-${...}.pdf`);
═══════════════════════════════════════════════════════════ */

/* ─── colour palette (mirrors CSS vars) ─── */
const C = {
  navy: [30, 58, 95],
  navyLight: [42, 80, 120],
  blue: [37, 99, 235],
  green: [22, 163, 74],
  greenDim: [220, 252, 231],
  amber: [217, 119, 6],
  amberDim: [254, 243, 199],
  red: [220, 38, 38],
  redDim: [254, 226, 226],
  teal: [13, 148, 132],
  tealDim: [204, 251, 241],
  purple: [124, 58, 237],
  purpleDim: [237, 233, 254],
  muted: [107, 122, 153],
  border: [226, 232, 242],
  bg: [240, 244, 250],
  cardBg: [247, 249, 253],
  altRow: [245, 248, 255],
  white: [255, 255, 255],
  text: [26, 34, 54],
  lightText: [80, 80, 80],
};
